import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePublicFabbriche, usePublicMarchi, usePublicServizi } from "@/hooks/usePublicEntities";

const FALLBACK_FABBRICHE = [
  { label: "Cioccolateria", slug: "cioccolateria" },
  { label: "Biscottificio", slug: "biscottificio" },
  { label: "Distilleria", slug: "distilleria" },
  { label: "Lab. Farmaceutico", slug: "laboratorio-farmaceutico" },
  { label: "Azienda Agricola", slug: "azienda-agricola" },
];
const FALLBACK_SERVIZI = [
  { label: "Private Label", slug: "private-label" },
  { label: "Hotellerie", slug: "hotellerie" },
  { label: "Ristorazione", slug: "ristorazione" },
  { label: "Regali Aziendali", slug: "regali-aziendali" },
  { label: "Esperienze", slug: "esperienze" },
];

const MinimalFooter = () => {
  const { t, language } = useLanguage();
  const { data: fabbriche } = usePublicFabbriche();
  const { data: marchi } = usePublicMarchi();
  const { data: servizi } = usePublicServizi();

  // Build marchi links: own + partner brands, then a single "Difesa" link
  const marchiItems = (marchi || [])
    .filter(m => m.show_in_menu && m.tipo !== 'difesa')
    .map(m => {
      const isExternal = !m.has_internal_page && m.website_url;
      return {
        label: m.nome,
        href: isExternal ? m.website_url! : `/brands/${m.slug}`,
        external: !!isExternal,
      };
    });
  marchiItems.push({
    label: language === 'it' ? "Marchi della Difesa" : "Defense Brands",
    href: "/marchi#marchi-difesa",
    external: false,
  });

  const links = [
    {
      title: t('nav.gruppo'),
      items: [
        { label: language === 'it' ? "Chi Siamo" : "About Us", href: "/gruppo#chi-siamo", external: false },
        { label: language === 'it' ? "I Valori delle Fabbriche" : "Our Values", href: "/gruppo#valori", external: false },
        { label: language === 'it' ? "Come operano le Fabbriche" : "How We Operate", href: "/gruppo#come-operano", external: false },
        { label: language === 'it' ? "Certificazioni" : "Certifications", href: "/certificazioni", external: false },
      ],
    },
    {
      title: t('nav.marchi'),
      items: marchiItems,
    },
    {
      title: t('nav.fabbriche'),
      items: (fabbriche && fabbriche.length ? fabbriche.map(f => ({ label: f.nome_breve || f.nome, slug: f.slug })) : FALLBACK_FABBRICHE).map(f => ({
        label: f.label,
        href: `/fabbriche/${f.slug}`,
        external: false,
      })),
    },
    {
      title: t('nav.progetti'),
      items: (servizi && servizi.length ? servizi.map(s => ({ label: s.nome, slug: s.slug })) : FALLBACK_SERVIZI).map(s => ({
        label: s.label,
        href: `/progetti/${s.slug}`,
        external: false,
      })),
    },
    {
      title: language === 'it' ? "Notizie" : "News",
      items: [
        { label: language === 'it' ? "Blog delle Fabbriche" : "Factory Blog", href: "/notizie/blog", external: false },
        { label: language === 'it' ? "Calendario Eventi" : "Event Calendar", href: "/notizie#eventi", external: false },
        { label: language === 'it' ? "Cartella Stampa" : "Press Kit", href: "/notizie#cartella-stampa", external: false },
        { label: language === 'it' ? "Contatti" : "Contact", href: "/contatti", external: false },
      ],
    },
  ];

  const sectionHref = (title: string) => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('gruppo') || titleLower.includes('group')) return "/gruppo";
    if (titleLower.includes('marchi') || titleLower.includes('brand')) return "/marchi";
    if (titleLower.includes('fabbr') || titleLower.includes('factor')) return "/fabbriche";
    if (titleLower.includes('proget') || titleLower.includes('project')) return "/progetti";
    if (titleLower.includes('certif')) return "/certificazioni";
    return "#";
  };

  return (
    <footer className="bg-footer text-footer-foreground border-t border-footer-foreground/15 pb-16 lg:pb-0">
      <div className="container mx-auto px-6 py-8 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 mb-8 md:mb-12">
          {links.map((section, index) => (
            <div key={index}>
              <h3 className="font-medium text-footer-foreground text-sm tracking-[0.1em] uppercase mb-6">
                <Link to={sectionHref(section.title)} className="hover:opacity-80 transition-colors duration-300">
                  {section.title}
                </Link>
              </h3>
              <ul className="space-y-3">
                {section.items.map((item, idx) => (
                  <li key={idx}>
                    {item.external ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-sm text-footer-foreground hover:opacity-80 transition-colors duration-300">
                        {item.label}
                      </a>
                    ) : (
                      <Link to={item.href} className="text-sm text-footer-foreground hover:opacity-80 transition-colors duration-300">
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-footer-foreground/15 pt-8">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center">
              <img
                src="/lovable-uploads/fabbriche-toscane-logo-footer.png"
                alt="Logo Fabbriche Toscane"
                width={254}
                height={40}
                className="h-8 md:h-10 w-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="text-center text-sm text-footer-foreground">
              <div className="mb-1">© 2026 Fabbriche Toscane Srl. {t('footer.rights')}</div>
              <div className="text-xs opacity-80">
                C.F. e P.IVA: 06363510485 • {language === 'it' ? 'Capitale Sociale' : 'Share Capital'}: € 700.000 • Codice Univoco: 5RUO82D
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-footer-foreground">
              <Link to="/privacy" className="hover:opacity-80 transition-colors">{t('footer.privacy')}</Link>
              <Link to="/termini-servizio" className="hover:opacity-80 transition-colors">{t('footer.termini')}</Link>
              <Link to="/cookie-policy" className="hover:opacity-80 transition-colors">{t('footer.cookie')}</Link>
              <Link to="/mappa-sito" className="hover:opacity-80 transition-colors">{language === 'it' ? 'Mappa del Sito' : 'Sitemap'}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MinimalFooter;
